import { useEffect, useState, Fragment } from "react";
import {
  Container,
  Title,
  Text,
  Table,
  Paper,
  Loader,
  Group,
  Stack,
  TextInput,
  Select,
  Button,
  Alert,
  ActionIcon,
  Box,
  Modal,
  Tooltip,
  Checkbox,
  Badge,
} from "@mantine/core";
import { IconPlus, IconMinus, IconAlertCircle, IconPencil, IconTrash } from "@tabler/icons-react";
import apiService from "../services/api";
import { weightToPieces, weightFromPieces } from "../utils/weightPieces";
import { usePrimaryTouched } from "../hooks/usePrimaryTouched";

function formatPercent(value) {
  return `${Number(value || 0).toFixed(2)}%`;
}

function formatDate(value) {
  if (!value) return "-";
  if (typeof value === "string") {
    const datePart = value.includes("T") ? value.split("T")[0] : value;
    const match = datePart.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!match) return new Date(value).toLocaleDateString();
    const [, y, m, d] = match;
    return `${d}.${m}.${y.slice(-2)}`;
  }
  return new Date(value).toLocaleDateString();
}

function todayInputValue() {
  return new Date().toISOString().slice(0, 10);
}

function calcPcs(qty, unit, weightValue, weightUnit) {
  if (!qty || !weightValue) return '';
  const pieces = weightToPieces(qty, unit, weightValue, weightUnit, { roundMode: 'round' });
  return pieces === null ? '' : pieces;
}

function formatPcs(batch) {
  if (batch.unit === 'pcs') return String(Math.round(Number(batch.quantity_produced)));
  if (batch.pieces_override != null) return String(Math.round(Number(batch.pieces_override)));
  const p = calcPcs(batch.quantity_produced, batch.unit, batch.weight_value, batch.weight_unit);
  return p !== '' ? String(p) : '-';
}

export default function AdminInventory() {
  const [summaryRows, setSummaryRows] = useState([]);
  const [expandedSkus, setExpandedSkus] = useState([]);
  const [productionRowsBySku, setProductionRowsBySku] = useState({});
  const [loadingSummary, setLoadingSummary] = useState(true);
  const [loadingProductions, setLoadingProductions] = useState({});
  const [formBySku, setFormBySku] = useState({});
  const [errorMessage, setErrorMessage] = useState("");
  const [editingBatch, setEditingBatch] = useState(null);
  const [editForm, setEditForm] = useState({ date: "", qty: "", unit: "pcs", pcs: "", reserved: false });
  const [savingEdit, setSavingEdit] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null); // { batchId, sku }
  const { markTouched, isTouched, untouch } = usePrimaryTouched();

  const getDefaultForm = (variant) => ({
    date: todayInputValue(),
    qty: "",
    unit: "pcs",
    pcs: "",
    reserved: false,
    ...(variant ? { _weightValue: variant.weight_value, _weightUnit: variant.weight_unit } : {})
  });

  const fetchSummary = async () => {
    try {
      setLoadingSummary(true);
      setErrorMessage("");
      const data = await apiService.getInventorySummary();
      setSummaryRows(data);
    } catch (err) {
      setErrorMessage(err.message || "Failed to load inventory summary");
    } finally {
      setLoadingSummary(false);
    }
  };

  useEffect(() => {
    fetchSummary();
  }, []);

  const ensureProductionRows = async (sku) => {
    if (productionRowsBySku[sku]) return;
    setLoadingProductions((prev) => ({ ...prev, [sku]: true }));
    try {
      const rows = await apiService.getInventoryProductions(sku);
      setProductionRowsBySku((prev) => ({ ...prev, [sku]: rows }));
    } catch (err) {
      setErrorMessage(
        err.message || `Failed to load production rows for ${sku}`
      );
      setProductionRowsBySku((prev) => ({ ...prev, [sku]: [] }));
    } finally {
      setLoadingProductions((prev) => ({ ...prev, [sku]: false }));
    }
  };

  const toggleSku = async (sku) => {
    const isExpanded = expandedSkus.includes(sku);
    if (isExpanded) {
      setExpandedSkus((prev) => prev.filter((item) => item !== sku));
      return;
    }
    setExpandedSkus((prev) => [...prev, sku]);
    await ensureProductionRows(sku);
  };

  const updateForm = (sku, key, value) => {
    // qty is the primary field: once the user types a qty, a pcs override
    // stops back-filling it (like quick order). Unit changes just re-derive
    // pcs and never freeze qty, so a pcs-first entry still auto-fills it.
    if (key === 'qty') markTouched(sku);
    setFormBySku((prev) => {
      const current = prev[sku];
      const variant = summaryRows.find(r => r.sku === sku);
      const currentWithWeight = current || getDefaultForm(variant ? { weight_value: variant.weight_value, weight_unit: variant.weight_unit } : {});
      const updated = { ...currentWithWeight, [key]: value };
      if ((key === 'qty' || key === 'unit') && currentWithWeight._weightValue) {
        if (key === 'unit' && value === 'pcs') {
          const pcsVal = currentWithWeight.pcs || calcPcs(currentWithWeight.qty, currentWithWeight.unit, currentWithWeight._weightValue, currentWithWeight._weightUnit);
          updated.pcs = pcsVal;
          updated.qty = typeof pcsVal === 'number' || pcsVal ? String(pcsVal) : currentWithWeight.qty;
        } else {
          updated.pcs = calcPcs(
            key === 'qty' ? value : currentWithWeight.qty,
            key === 'unit' ? value : currentWithWeight.unit,
            currentWithWeight._weightValue,
            currentWithWeight._weightUnit
          );
        }
      } else if (key === 'pcs' && currentWithWeight._weightValue && currentWithWeight.unit !== 'pcs' && !isTouched(sku)) {
        const weight = weightFromPieces(value, currentWithWeight._weightValue, currentWithWeight._weightUnit, currentWithWeight.unit);
        if (weight !== null) updated.qty = String(weight);
      }
      return { ...prev, [sku]: updated };
    });
  };

  const addProductionRow = async (sku) => {
    const form = formBySku[sku] || getDefaultForm();
    if (!form.date || !form.qty) {
      setErrorMessage(
        "Please provide date and quantity before adding a production row."
      );
      return;
    }
    try {
      setErrorMessage("");
      const piecesOverride = form.unit !== 'pcs' && form.pcs ? Number(form.pcs) : undefined;
      const created = await apiService.createInventoryProduction(sku, {
        production_date: form.date,
        quantity_produced: Number(form.qty),
        unit: form.unit || "pcs",
        pieces_override: piecesOverride,
        reserved: Boolean(form.reserved),
      });
      setProductionRowsBySku((prev) => ({
        ...prev,
        [sku]: [created.row, ...(prev[sku] || [])],
      }));
      setFormBySku((prev) => ({ ...prev, [sku]: getDefaultForm(summaryRows.find(r => r.sku === sku)) }));
      await fetchSummary();
    } catch (err) {
      setErrorMessage(err.message || "Failed to add production row");
    }
  };

  const openEditModal = (batch) => {
    const dateStr = batch.production_date
      ? typeof batch.production_date === "string"
        ? batch.production_date.includes("T")
          ? batch.production_date.split("T")[0]
          : batch.production_date
        : new Date(batch.production_date).toISOString().slice(0, 10)
      : "";
    const pcsVal = batch.pieces_override != null
      ? String(Math.round(Number(batch.pieces_override)))
      : calcPcs(batch.quantity_produced, batch.unit, batch.weight_value, batch.weight_unit);
    untouch(batch.id);
    setEditingBatch(batch);
    setEditForm({
      date: dateStr,
      qty: String(batch.quantity_produced),
      unit: batch.unit,
      pcs: pcsVal !== '' ? String(pcsVal) : '',
      reserved: Boolean(batch.reserved),
      _weightValue: batch.weight_value,
      _weightUnit: batch.weight_unit
    });
  };

  const saveEdit = async () => {
    if (!editingBatch) return;
    if (!editForm.date || !editForm.qty) {
      setErrorMessage("Please provide date and quantity.");
      return;
    }
    try {
      setSavingEdit(true);
      setErrorMessage("");
      const piecesOverride = editForm.unit !== 'pcs' && editForm.pcs ? Number(editForm.pcs) : undefined;
      const updated = await apiService.updateInventoryProduction(editingBatch.id, {
        production_date: editForm.date,
        quantity_produced: Number(editForm.qty),
        unit: editForm.unit || "pcs",
        pieces_override: piecesOverride,
        reserved: Boolean(editForm.reserved),
      });
      setProductionRowsBySku((prev) => {
        const sku = editingBatch.sku;
        const rows = prev[sku] || [];
        return { ...prev, [sku]: rows.map((r) => (r.id === editingBatch.id ? { ...r, ...updated.row } : r)) };
      });
      setEditingBatch(null);
      await fetchSummary();
    } catch (err) {
      setErrorMessage(err.message || "Failed to update production row");
    } finally {
      setSavingEdit(false);
    }
  };

  const handleDeleteProduction = async (batchId, sku) => {
    try {
      setErrorMessage("");
      await apiService.deleteInventoryProduction(batchId);
      setProductionRowsBySku((prev) => ({
        ...prev,
        [sku]: (prev[sku] || []).filter((r) => r.id !== batchId),
      }));
      await fetchSummary();
      setDeleteConfirm(null);
    } catch (err) {
      setErrorMessage(err.message || "Failed to delete production row");
      setDeleteConfirm(null);
    }
  };

  return (
    <Container fluid py="xl">
      <Stack mb="xl">
        <Title order={2}>Inventory Management</Title>
        <Text c="dimmed">
          Track current stock, all-time sold amount and sold percentage by
          product.
        </Text>
      </Stack>

      {errorMessage && (
        <Alert
          icon={<IconAlertCircle size={16} />}
          title="Error"
          color="red"
          mb="md"
        >
          {errorMessage}
        </Alert>
      )}

      {loadingSummary ? (
        <Loader />
      ) : (
        <Paper withBorder>
          <caption className="sr-only">Inventory summary by SKU with production rows expandable inline</caption>
          <Table highlightOnHover verticalSpacing="md" horizontalSpacing="md">
            <Table.Thead>
              <Table.Tr>
                <Table.Th scope="col">Product</Table.Th>
                <Table.Th scope="col">Online Stock</Table.Th>
                <Table.Th scope="col">Reserved</Table.Th>
                <Table.Th scope="col" title="Ordered beyond available stock, to be produced in the next batch">Committed</Table.Th>
                <Table.Th scope="col">Total</Table.Th>
                <Table.Th scope="col">Sold Amt (All Time)</Table.Th>
                <Table.Th scope="col">Sold % (All Time)</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {summaryRows.length === 0 ? (
                <Table.Tr>
                  <Table.Td colSpan={7}>No products found.</Table.Td>
                </Table.Tr>
              ) : (
                summaryRows.map((row) => {
                  const isExpanded = expandedSkus.includes(row.sku);
                  const skuForm = formBySku[row.sku] || getDefaultForm({ weight_value: row.weight_value, weight_unit: row.weight_unit });
                  return (
                    <Fragment key={row.sku}>
                      <Table.Tr
                        onClick={() => toggleSku(row.sku)}
                        style={{
                          cursor: "pointer",
                          backgroundColor: isExpanded
                            ? "var(--mantine-color-gray-0)"
                            : "transparent",
                        }}
                      >
                        <Table.Td>
                          <Group gap="sm">
                            <ActionIcon
                              size={20}
                              variant="filled"
                              color="gray.2"
                              c="dark.9"
                              radius="xl"
                            >
                              {isExpanded ? (
                                <IconMinus size={14} aria-hidden="true" />
                              ) : (
                                <IconPlus size={14} aria-hidden="true" />
                              )}
                            </ActionIcon>
                            <Box>
                              <Text fw={700}>{row.product_name}</Text>
                              <Text size="xs" c="dimmed">
                                {row.sku}
                              </Text>
                            </Box>
                          </Group>
                        </Table.Td>
                        <Table.Td>{row.current_stock}</Table.Td>
                        <Table.Td>
                          {Number(row.reserved_stock) > 0 ? (
                            <Badge color="orange" variant="light">{row.reserved_stock}</Badge>
                          ) : (
                            <Text c="dimmed">0</Text>
                          )}
                        </Table.Td>
                        <Table.Td>
                          {Number(row.committed_stock) > 0 ? (
                            <Badge color="grape" variant="light">{row.committed_stock}</Badge>
                          ) : (
                            <Text c="dimmed">0</Text>
                          )}
                        </Table.Td>
                        <Table.Td>{Number(row.current_stock || 0) + Number(row.reserved_stock || 0)}</Table.Td>
                        <Table.Td>{row.sold_amount}</Table.Td>
                        <Table.Td>{formatPercent(row.sold_percent)}</Table.Td>
                      </Table.Tr>

                      {isExpanded && (
                        <Table.Tr>
                          <Table.Td
                            colSpan={7}
                            style={{
                              backgroundColor: "var(--mantine-color-gray-0)",
                            }}
                          >
                            <Stack p="md">
                              <Table
                                withColumnBorders
                                withTableBorder
                                size="sm"
                              >
                                <caption className="sr-only">Production batches for {row.product_name}</caption>
                                <Table.Thead>
                                  <Table.Tr>
                                    <Table.Th scope="col">Date</Table.Th>
                                    <Table.Th scope="col">Qty Produced</Table.Th>
                                    <Table.Th scope="col">Unit</Table.Th>
                                    {row.weight_unit && <Table.Th scope="col">Pcs</Table.Th>}
                                    <Table.Th scope="col">Lot Number</Table.Th>
                                    <Table.Th scope="col">Reserved</Table.Th>
                                    <Table.Th scope="col">Remaining</Table.Th>
                                    <Table.Th scope="col">Sold %</Table.Th>
                                    <Table.Th scope="col"><span className="sr-only">Actions</span></Table.Th>
                                  </Table.Tr>
                                </Table.Thead>
                                <Table.Tbody>
                                  {loadingProductions[row.sku] ? (
                                    <Table.Tr>
                                      <Table.Td colSpan={row.weight_unit ? 9 : 8}>
                                        <span role="status" aria-live="polite">Loading…</span>
                                      </Table.Td>
                                    </Table.Tr>
                                  ) : (
                                    (productionRowsBySku[row.sku] || []).map(
                                      (batch) => (
                                        <Table.Tr key={batch.id}>
                                          <Table.Td>
                                            {formatDate(batch.production_date)}
                                          </Table.Td>
                                          <Table.Td>
                                            {batch.quantity_produced}
                                          </Table.Td>
                                          <Table.Td>{batch.unit}</Table.Td>
                                          {row.weight_unit && (
                                            <Table.Td>
                                              {formatPcs(batch)}
                                            </Table.Td>
                                          )}
                                          <Table.Td>
                                            {batch.lot_number}
                                          </Table.Td>
                                          <Table.Td>
                                            {batch.reserved ? (
                                              <Badge color="orange" variant="light">Reserved</Badge>
                                            ) : (
                                              <Badge color="gray" variant="light">Online</Badge>
                                            )}
                                          </Table.Td>
                                          <Table.Td>
                                            {batch.remaining != null ? batch.remaining : "-"}
                                          </Table.Td>
                                          <Table.Td>
                                            {formatPercent(batch.sold_percent)}
                                          </Table.Td>
                                          <Table.Td>
                                            <Group gap="xs" wrap="nowrap">
                                              <Tooltip label="Correct row">
                                                <ActionIcon
                                                  variant="subtle"
                                                  color="gray"
                                                  size="sm"
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    openEditModal({ ...batch, sku: row.sku });
                                                  }}
                                                  aria-label={`Correct production row for ${row.product_name} dated ${formatDate(batch.production_date)}`}
                                                >
                                                  <IconPencil size={14} aria-hidden="true" />
                                                </ActionIcon>
                                              </Tooltip>
                                              <Tooltip label="Delete row">
                                                <ActionIcon
                                                  variant="subtle"
                                                  color="red"
                                                  size="sm"
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                      setDeleteConfirm({ batchId: batch.id, sku: row.sku });
                                                    }}
                                                  aria-label={`Delete production row for ${row.product_name} dated ${formatDate(batch.production_date)}`}
                                                >
                                                  <IconTrash size={14} aria-hidden="true" />
                                                </ActionIcon>
                                              </Tooltip>
                                            </Group>
                                          </Table.Td>
                                        </Table.Tr>
                                      )
                                    )
                                  )}
                                </Table.Tbody>
                              </Table>
                              <Group align="flex-end">
                                <TextInput
                                  type="date"
                                  label="Date"
                                  value={skuForm.date}
                                  onChange={(e) =>
                                    updateForm(row.sku, "date", e.target.value)
                                  }
                                />
                                <TextInput
                                  type="number"
                                  label="Qty"
                                  value={skuForm.qty}
                                  onChange={(e) =>
                                    updateForm(row.sku, "qty", e.target.value)
                                  }
                                />
                                <Select
                                  label="Unit"
                                  data={["pcs", "kg", "g", "L"]}
                                  value={skuForm.unit}
                                  onChange={(v) =>
                                    updateForm(row.sku, "unit", v)
                                  }
                                />
                                {row.weight_unit && skuForm.unit !== 'pcs' && (
                                  <TextInput
                                    type="number"
                                    label="Pcs (override)"
                                    value={skuForm.pcs}
                                    onChange={(e) =>
                                      updateForm(row.sku, "pcs", e.target.value)
                                    }
                                    style={{ maxWidth: 100 }}
                                  />
                                )}
                                <Checkbox
                                  label="Reserved"
                                  description="Hold for admin-taken orders only"
                                  checked={Boolean(skuForm.reserved)}
                                  onChange={(e) =>
                                    updateForm(row.sku, "reserved", e.target.checked)
                                  }
                                  mt={26}
                                />
                                <Button
                                  onClick={() => addProductionRow(row.sku)}
                                >
                                  + Add Inventory
                                </Button>
                              </Group>
                            </Stack>
                          </Table.Td>
                        </Table.Tr>
                      )}
                    </Fragment>
                  );
                })
              )}
            </Table.Tbody>
          </Table>
        </Paper>
      )}

      <Modal
        opened={!!editingBatch}
        onClose={() => !savingEdit && setEditingBatch(null)}
        title="Correct Production Row"
        centered
      >
        <Stack>
          {errorMessage && (
            <div role="alert" style={{ background: '#fff5f5', color: '#c9184a', padding: '0.5rem 0.75rem', borderRadius: 6 }}>
              {errorMessage}
            </div>
          )}
          <TextInput
            type="date"
            label="Date"
            value={editForm.date}
            onChange={(e) => setEditForm((prev) => ({ ...prev, date: e.target.value }))}
          />
          <TextInput
            type="number"
            label="Qty Produced"
            value={editForm.qty}
            onChange={(e) => {
              const val = e.target.value;
              markTouched(editingBatch.id);
              setEditForm((prev) => {
                const next = { ...prev, qty: val };
                if (prev._weightValue && prev.unit !== 'pcs') {
                  next.pcs = calcPcs(val, prev.unit, prev._weightValue, prev._weightUnit);
                }
                return next;
              });
            }}
          />
          <Select
            label="Unit"
            data={["pcs", "kg", "g", "L"]}
            value={editForm.unit}
            onChange={(v) => {
              setEditForm((prev) => {
                const next = { ...prev, unit: v };
                if (v !== 'pcs' && prev._weightValue) {
                  next.pcs = calcPcs(prev.qty, v, prev._weightValue, prev._weightUnit);
                }
                return next;
              });
            }}
          />
          {editForm._weightValue && editForm.unit !== 'pcs' && (
            <TextInput
              type="number"
              label="Pcs (override)"
              value={editForm.pcs}
              onChange={(e) =>
                setEditForm((prev) => {
                  const next = { ...prev, pcs: e.target.value };
                  if (prev._weightValue && prev.unit !== 'pcs' && !isTouched(editingBatch.id)) {
                    const weight = weightFromPieces(e.target.value, prev._weightValue, prev._weightUnit, prev.unit);
                    if (weight !== null) next.qty = String(weight);
                  }
                  return next;
                })
              }
            />
          )}
          <Checkbox
            label="Reserved"
            description="Hold for admin-taken orders only; not released to the online store"
            checked={Boolean(editForm.reserved)}
            onChange={(e) =>
              setEditForm((prev) => ({ ...prev, reserved: e.target.checked }))
            }
          />
          <Group justify="flex-end" mt="md">
            <Button variant="default" onClick={() => setEditingBatch(null)} disabled={savingEdit}>
              Cancel
            </Button>
            <Button onClick={saveEdit} loading={savingEdit}>
              Save
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal
        opened={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        title="Delete Production Row?"
        centered
      >
        <Text mb="md">This will reduce stock. Are you sure?</Text>
        <Group justify="flex-end">
          <Button variant="default" onClick={() => setDeleteConfirm(null)}>
            Cancel
          </Button>
          <Button color="red" onClick={() => handleDeleteProduction(deleteConfirm.batchId, deleteConfirm.sku)}>
            Delete
          </Button>
        </Group>
      </Modal>
    </Container>
  );
}
